import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import Bind from 'lodash-decorators/bind';
import Debounce from 'lodash-decorators/debounce';

import { isOnMobile } from 'utils/device-utils';
import LanguageService from 'services/Language.service';

//styles
import styles from './ArticlesPage.module.scss';

import { LOAD_STATES } from 'common/consts/common';

//components
import { Container, Title } from 'common/components/Layout/Layout';
import PrimaryButtonComponent from 'common/components/PrimaryButtonComponent/PrimaryButtonComponent';
import Filters from 'common/components/Filters/Filters';
import ArticleNoteMd from './components/ArticleNoteMd/AricleNoteMd';
import ArticleNoteSm from './components/ArticleNoteSm/ArticleNoteSm';
import PageNotFound from 'views/PageNotFound/PageNotFoundView';
import CategoryList from 'common/components/CategoryList/CategoryList';
import TagsVerticalList from 'common/components/TagsVerticalList/TagsVerticalList';
import ContactPerson from 'common/components/ContactPerson/ContactPerson';

import TaxonomyStore from 'stores/Taxonomy.store';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import ContentStore from 'stores/Content.store';
import InjectMetaTags from 'common/components/InjectMetaTags/InjectMetaTags';

import Placeholder from 'common/components/Placeholder/Placeholder';
import ArticleBig from 'common/components/Placeholder/components/ArticleBig/ArticleBig';
import ArticleSmall from 'common/components/Placeholder/components/ArticleSmall/ArticleSmall';
import ContentBar from 'common/components/Placeholder/components/ContentBar/ContentBar';
import ReturnToInvestors from '../ReturnToInvestors/ReturnToInvestors';

@injectIntl
@withRouter
@observer
export default class ArticlesPage extends Component {
  taxonomies = TaxonomyStore;

  state = {
    isMobile: isOnMobile()
  };

  loadState = observable.box('');
  isExploited = false;

  currentLanguage = LanguageService.getLang();

  async componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
    this.fetchLatestNews(this.props.criteria);
    this.loadState.set(LOAD_STATES.LOADED);
  }

  componentWillUnmount() {
    if (!this.props.selectedFilter) {
      return;
    }
    window.removeEventListener('resize', this.updateDimensions);
  }

  async fetchLatestNews(criteria) {
    this.loadState.set(LOAD_STATES.LOADING);
    await this.props.fetchLatestNews(criteria);
    this.loadState.set(LOAD_STATES.LOADED);
  }

  @Bind()
  onYearChange(year) {
    const {
      history,
      categorySlug,
      match: {
        params: { lang }
      }
    } = this.props;
    const langPrefix = lang ? `/${lang}` : '';

    history.push(
      `${langPrefix}${categorySlug[this.currentLanguage]}/${year.value}`
    );
  }

  @computed
  get isLoaded() {
    return this.loadState.get() === LOAD_STATES.LOADED;
  }

  renderLatestNews() {
    const {
      onCategoryChange,
      articleNoteMdMessages,
      categoryPath,
      categorySlug,
      viewWithOnlyNews
    } = this.props;
    return (
      <div className={styles.ArticleNoteMdContainer}>
        <Placeholder
          component={<ArticleBig />}
          repeat={4}
          ready={this.isLoaded || !!this.props.latestNews.length}
        >
          {this.props.latestNews &&
            this.props.latestNews.map(news => {
              return (
                <ArticleNoteMd
                  key={news.system.codename}
                  news={news}
                  filter={onCategoryChange}
                  messages={articleNoteMdMessages}
                  categoryPath={categoryPath}
                  categorySlug={categorySlug}
                  viewWithOnlyNews={viewWithOnlyNews}
                />
              );
            })}
        </Placeholder>
      </div>
    );
  }

  renderOtherNews() {
    let otherNews = this.props.otherNews;
    const {
      onCategoryChange,
      articleNoteSmMessages,
      categoryPath,
      categorySlug,
      viewWithOnlyNews
    } = this.props;

    if (this.props.latestNews.length) {
      otherNews = otherNews.filter(
        el => el.system.id !== this.props.latestNews[0].system.id
      );
    }

    return (
      <div className={styles.SmallArticleMargin}>
        <Placeholder
          component={<ArticleSmall />}
          repeat={2}
          ready={this.isLoaded}
        >
          {otherNews.map(news => {
            return (
              <ArticleNoteSm
                key={news.system.codename}
                news={news}
                filter={onCategoryChange}
                messages={articleNoteSmMessages}
                categoryPath={categoryPath}
                categorySlug={categorySlug}
                viewWithOnlyNews={viewWithOnlyNews}
              />
            );
          })}
        </Placeholder>
      </div>
    );
  }

  renderDesktopView(categories) {
    const {
      tagsList,
      filtersList,
      onCategoryChange,
      noCategoryList,
      criteria: { category, publishYear },
      person,
      viewWithOnlyNews,
      withReturnToInvestors
    } = this.props;
    const { isExploited, onYearChange } = this;

    return (
      <div className={styles.ArticlesPage}>
        <Container>
          <div className={styles.MainHeading}>
            <div>
              <Title
                title={this.props.intl.formatMessage(
                  this.props.messages.heading
                )}
                className={styles.MainHeadingWithFilters}
              />
              {withReturnToInvestors && <ReturnToInvestors />}
            </div>
            <Filters
              filters={filtersList}
              onSelect={onYearChange}
              activeFilter={publishYear}
            />
          </div>
        </Container>
        <Container>
          <div className={styles.ColumnLastAddedArticles}>
            <span className={styles.LastAddedTitle}>
              {this.props.intl.formatMessage(this.props.messages.lastAdded)}
            </span>
            {this.isLoaded &&
              !this.props.latestNews.length && (
                <p className={styles.NoResults}>
                  {this.props.intl.formatMessage(
                    this.props.messages.nothingToShow
                  )}
                </p>
              )}
            {this.props.latestNews && this.renderLatestNews()}
            <div className={styles.PrimaryButtonStyle}>
              {!isExploited && (
                <PrimaryButtonComponent
                  className={styles.PrimaryButton}
                  text={this.props.intl.formatMessage(
                    this.props.messages.button
                  )}
                  onClick={this.props.showMore}
                  isBusy={!this.isLoaded}
                />
              )}
            </div>
          </div>
          {!viewWithOnlyNews && (
            <>
              <div className={`${styles.SidebarOthers}`}>
                <h3 className={styles.OtherTitle}>
                  {this.props.intl.formatMessage(this.props.messages.others)}
                </h3>
                {this.renderOtherNews()}
              </div>
              <div
                className={`${styles.SidebarCategories} ${
                  styles.CaseStudiesColumnPush
                }`}
              >
                {!noCategoryList && (
                  <>
                    <h3 className={styles.CategoryTitle}>
                      {this.props.intl.formatMessage(
                        this.props.messages.categories
                      )}
                    </h3>
                    <Placeholder
                      component={
                        <ContentBar className={styles.ContentBarPlaceholder} />
                      }
                      ready={categories.length > 1}
                      repeat={6}
                    >
                      <CategoryList
                        categories={categories}
                        onSelect={onCategoryChange}
                        activeOption={category}
                      />
                    </Placeholder>
                  </>
                )}
                <div>
                  <Placeholder
                    component={
                      <ContentBar className={styles.ContentBarPlaceholder} />
                    }
                    ready={!!tagsList.length && ContentStore.isLoaded}
                    repeat={3}
                  >
                    <TagsVerticalList tags={tagsList} />
                    {person && (
                      <>
                        <h3 className={styles.CategoryTitle}>
                          {this.props.intl.formatMessage(
                            this.props.messages.journalistContact
                          )}
                        </h3>
                        {ContentStore.isLoaded && (
                          <ContactPerson
                            person={person}
                            className={styles.Margin}
                          />
                        )}
                      </>
                    )}
                  </Placeholder>
                </div>
              </div>
            </>
          )}
        </Container>
      </div>
    );
  }

  renderMobileView(categories) {
    const {
      messages,
      intl,
      filtersList,
      onCategoryChange,
      person,
      noCategoryList,
      withReturnToInvestors,
      criteria: { category, publishYear }
    } = this.props;
    const { isExploited, onYearChange } = this;

    return (
      <div className={styles.ArticlesPage}>
        <Container>
          <div className={styles.MainHeading}>
            <h1 className={styles.MainHeadingWithFilters}>
              {intl.formatMessage(messages.heading)}
            </h1>
          </div>
          <div className={styles.FiltersBox}>
            <div className={styles.FilterMobile}>
              <h3 className={styles.CategoryTitle}>
                {intl.formatMessage(messages.publicationDate)}
              </h3>
              <Filters
                filters={filtersList}
                onSelect={onYearChange}
                activeFilter={publishYear}
              />
            </div>
            {!noCategoryList && (
              <div className={styles.FilterMobile}>
                <h3 className={styles.CategoryTitle}>
                  {intl.formatMessage(messages.categories)}
                </h3>
                <CategoryList
                  categories={categories}
                  onSelect={onCategoryChange}
                  activeOption={category}
                />
              </div>
            )}
          </div>
        </Container>
        {withReturnToInvestors && (
          <Container>
            <ReturnToInvestors />
          </Container>
        )}
        <Container>
          <div className={`${styles.ColumnLastAddedArticles} `}>
            <span className={styles.LastAddedTitle}>
              {intl.formatMessage(messages.lastAdded)}
            </span>
            {this.isLoaded &&
              !this.props.latestNews.length && (
                <p className={styles.NoResults}>
                  {intl.formatMessage(messages.nothingToShow)}
                </p>
              )}
            {this.renderLatestNews()}
            <div className={styles.PrimaryButtonStyle}>
              {!isExploited && (
                <PrimaryButtonComponent
                  className={styles.PrimaryButton}
                  text={this.props.intl.formatMessage(messages.button)}
                  onClick={this.props.showMore}
                  isBusy={!this.isLoaded}
                />
              )}
            </div>
          </div>
          <div className={`${styles.SidebarOthers}`}>
            <h3 className={styles.OtherTitle}>
              {intl.formatMessage(messages.others)}
            </h3>
            {this.renderOtherNews()}
            <h3 className={styles.CategoryTitle}>
              {intl.formatMessage(messages.journalistContact)}
            </h3>
            {ContentStore.isLoaded &&
              person && (
                <ContactPerson person={person} className={styles.Margin} />
              )}
          </div>
        </Container>
      </div>
    );
  }

  render() {
    const { categoryList, seoContent } = this.props;
    if (!this.props.selectedFilter) {
      return <PageNotFound />;
    }

    return (
      <div>
        {ContentStore.isLoaded && <InjectMetaTags {...seoContent} />}
        {this.state.isMobile
          ? this.renderMobileView(categoryList)
          : this.renderDesktopView(categoryList)}
      </div>
    );
  }

  @Bind()
  @Debounce(200)
  updateDimensions() {
    this.setState({
      isMobile: isOnMobile()
    });
  }
}



// WEBPACK FOOTER //
// ./src/common/components/ArticlesPage/ArticlesPage.js